import React, { useState, useCallback, useRef } from 'react';
import { arrayOf, func, node, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { formatCurrencyMajorUnit } from '../../../util/currency';

import IconPlus from '../IconPlus/IconPlus';
import PriceFilterForm from '../PriceFilterForm/PriceFilterForm';

import css from './PriceFilterPlain.module.css';

const RADIX = 10;

const getPriceQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

// Parse value, which should look like "0,1000"
const parse = priceRange => {
  const [minPrice, maxPrice] = priceRange
    ? priceRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  return !!priceRange && minPrice != null && maxPrice != null ? { minPrice, maxPrice } : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { minPrice, maxPrice } = range || {};
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  const value = minPrice != null && maxPrice != null ? `${minPrice},${maxPrice}` : null;
  return { [queryParamName]: value };
};

function PriceFilterPlain({
  rootClassName = null,
  className = null,
  id,
  label,
  queryParamNames,
  onSubmit,
  initialValues = null,
  min,
  max,
  step = 1,
  marketplaceCurrency,
}) {
  const [isOpen, setIsOpen] = useState(true);
  const filterContentRef = useRef(null);

  const handleChange = useCallback(
    values => {
      const priceQueryParamName = getPriceQueryParamName(queryParamNames);
      onSubmit(format(values, priceQueryParamName));
    },
    [onSubmit, queryParamNames]
  );

  const handleClear = useCallback(
    e => {
      e.preventDefault();
      const priceQueryParamName = getPriceQueryParamName(queryParamNames);
      onSubmit(format(null, priceQueryParamName));
    },
    [onSubmit, queryParamNames]
  );

  const toggleIsOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const classes = classNames(rootClassName || css.root, className);

  const priceQueryParam = getPriceQueryParamName(queryParamNames);
  const initialPrice = initialValues ? parse(initialValues[priceQueryParam]) : {};
  const { minPrice, maxPrice } = initialPrice || {};

  const hasValue = value => value != null;
  const hasInitialValues = initialValues && hasValue(minPrice) && hasValue(maxPrice);

  // Inline i18n: label for selected
  const labelSelection = hasInitialValues
    ? ` ${formatCurrencyMajorUnit(null, marketplaceCurrency, minPrice)} - ${formatCurrencyMajorUnit(
        null,
        marketplaceCurrency,
        maxPrice
      )}`
    : null;

  return (
    <div className={classes}>
      <div className={css.filterHeader}>
        <button type="button" className={css.labelButton} onClick={toggleIsOpen}>
          <span className={css.labelButtonContent}>
            <span className={css.labelWrapper}>
              <span className={css.label}>
                {label}
                {labelSelection ? (
                  <>
                    <span>{': '}</span>
                    <span className={css.labelSelected}>{labelSelection}</span>
                  </>
                ) : null}
              </span>
            </span>
            <span className={css.openSign}>
              <IconPlus isOpen={isOpen} isSelected={hasInitialValues} />
            </span>
          </span>
        </button>
      </div>
      <div className={css.formWrapper}>
        <PriceFilterForm
          id={id}
          initialValues={hasInitialValues ? initialPrice : { minPrice: min, maxPrice: max }}
          onChange={handleChange}
          contentRef={filterContentRef}
          min={min}
          max={max}
          step={step}
          liveEdit
          isOpen={isOpen}
          isInSideBar
          style={{ minWidth: '160px' }}
        >
          <button className={css.clearButton} onClick={handleClear}>
            Clear
          </button>
        </PriceFilterForm>
      </div>
    </div>
  );
}



PriceFilterPlain.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  queryParamNames: arrayOf(string).isRequired,
  onSubmit: func.isRequired,
  initialValues: shape({
    price: string,
  }),
  min: number.isRequired,
  max: number.isRequired,
  step: number,
  marketplaceCurrency: string.isRequired,
};

export default PriceFilterPlain;
